import React from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import { HomePage } from './components/home';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { createCustomColumns, CustomFilters } from './components/catalog/CustomCatalogPage';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  ScaffolderFieldExtensions,
  createScaffolderFieldExtension,
} from '@backstage/plugin-scaffolder-react';
import {
  CustomTemplateCard,
  TenantAppPicker,
  GithubRepoPicker,
  GithubBranchPicker,
  GithubWorkflowInputPicker,
} from './components/scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  ProxiedSignInPage,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { configApiRef, microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';

const TenantAppPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'TenantAppPicker',
    component: TenantAppPicker,
  }),
);

const GithubRepoPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GithubRepoPicker',
    component: GithubRepoPicker,
  }),
);

const GithubBranchPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GithubBranchPicker',
    component: GithubBranchPicker,
  }),
);

const GithubWorkflowInputPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GithubWorkflowInputPicker',
    component: GithubWorkflowInputPicker,
  }),
);

// Reads app.localDevelopment from runtime config (served by the backend from YAML files).
// When true, auto-signs in as guest — no Azure credentials required.
const AppSignInPage = (props: any) => {
  const configApi = useApi(configApiRef);
  const isLocal = configApi.getOptionalBoolean('app.localDevelopment') === true;
  if (isLocal) {
    return <ProxiedSignInPage {...props} provider="guest" />;
  }
  return (
    <SignInPage
      {...props}
      auto
      provider={{
        id: 'microsoft',
        title: 'Microsoft Entra ID',
        message: 'Sign in with your Microsoft account',
        apiRef: microsoftAuthApiRef,
      }}
    />
  );
};

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => <AppSignInPage {...props} />,
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          columns={createCustomColumns()}
          filters={<CustomFilters />}
          tableOptions={{
            padding: 'dense',
            pageSize: 20,
            pageSizeOptions: [10, 20, 50, 100],
          }}
        />
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          components={{
            TemplateCardComponent: CustomTemplateCard,
          }}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <TenantAppPickerExtension />
        <GithubRepoPickerExtension />
        <GithubBranchPickerExtension />
        <GithubWorkflowInputPickerExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
